import React, { useState, useEffect } from 'react';
import { Button, Form, Alert, Spinner, Row, Col } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { ForemanArea, ServiceArea } from '../../../services/areasService';

interface ForemanAreaModalProps {
  show: boolean;
  onHide: () => void;
  foremanArea: ForemanArea | null;
  serviceAreas: ServiceArea[];
  onSave: (foremanAreaData: Partial<ForemanArea> & { contractAreaIds?: number[] }) => Promise<void>;
}

const ForemanAreaModal: React.FC<ForemanAreaModalProps> = ({
  show,
  onHide,
  foremanArea,
  serviceAreas,
  onSave
}) => {
  const [formData, setFormData] = useState({
    name: '',
    serviceAreaId: '',
    latitude: '',
    longitude: '',
    address: '',
    phone: '',
    email: '',
    omitFromWordpressMap: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (foremanArea) {
      setFormData({
        name: foremanArea.name || '',
        serviceAreaId: foremanArea.serviceArea?.id?.toString() || String(foremanArea.serviceAreaId || ''),
        latitude:
          foremanArea.latitude !== undefined && foremanArea.latitude !== null
            ? String(foremanArea.latitude)
            : '',
        longitude:
          foremanArea.longitude !== undefined && foremanArea.longitude !== null
            ? String(foremanArea.longitude)
            : '',
        address: foremanArea.address || '',
        phone: foremanArea.phone || '',
        email: foremanArea.email || '',
        omitFromWordpressMap: foremanArea.omitFromWordpressMap === true
      });
    } else {
      setFormData({
        name: '',
        serviceAreaId: '',
        latitude: '',
        longitude: '',
        address: '',
        phone: '',
        email: '',
        omitFromWordpressMap: false
      });
    }
    setError(null);
  }, [foremanArea, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const serviceAreaId = parseInt(formData.serviceAreaId, 10);
      const payload: Parameters<ForemanAreaModalProps['onSave']>[0] = {
        name: formData.name,
        serviceAreaId,
        latitude: formData.latitude.trim().length ? Number(formData.latitude) : null,
        longitude: formData.longitude.trim().length ? Number(formData.longitude) : null,
        address: formData.address.trim().length ? formData.address : null,
        phone: formData.phone.trim().length ? formData.phone : null,
        email: formData.email.trim().length ? formData.email : null,
        omitFromWordpressMap: formData.omitFromWordpressMap,
        contractAreaIds: foremanArea?.contractAreas?.map((c) => c.id) ?? []
      };
      await onSave(payload);
      onHide();
    } catch (err) {
      console.error('Error saving foreman area:', err);
      setError('Failed to save foreman area. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isFormValid = formData.name.trim().length > 0 && formData.serviceAreaId !== '';

  const modalFooter = (
    <>
      <Button variant="secondary" onClick={onHide} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        disabled={!isFormValid || loading}
        onClick={handleSubmit}
      >
        {loading ? (
          <>
            <Spinner animation="border" size="sm" className="tw-mr-2" />
            Saving...
          </>
        ) : (
          <>
            <i className="bi bi-check-lg tw-mr-2"></i>
            {foremanArea ? 'Update' : 'Create'} Foreman Area
          </>
        )}
      </Button>
    </>
  );

  return (
    <ResponsiveModal
      isOpen={show}
      onClose={onHide}
      title={foremanArea ? 'Edit Foreman Area' : 'Create New Foreman Area'}
      footer={modalFooter}
    >
      {error && (
        <Alert variant="danger">{error}</Alert>
      )}

      <Form.Group className="tw-mb-3">
        <Form.Label>Name *</Form.Label>
        <Form.Control
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          placeholder="Enter foreman area name"
          required
          disabled={loading}
        />
      </Form.Group>

      <Form.Group className="tw-mb-3">
        <Form.Label>Service Area *</Form.Label>
        <Form.Select
          value={formData.serviceAreaId}
          onChange={(e) => handleInputChange('serviceAreaId', e.target.value)}
          required
          disabled={loading}
        >
          <option value="">Select a service area</option>
          {serviceAreas.map(serviceArea => (
            <option key={serviceArea.id} value={serviceArea.id}>
              {serviceArea.name} {serviceArea.code && `(${serviceArea.code})`}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Row className="g-3 tw-mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Latitude</Form.Label>
            <Form.Control
              type="number"
              inputMode="decimal"
              value={formData.latitude}
              onChange={(e) => handleInputChange('latitude', e.target.value)}
              placeholder="e.g. 50.12345"
              disabled={loading}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Longitude</Form.Label>
            <Form.Control
              type="number"
              inputMode="decimal"
              value={formData.longitude}
              onChange={(e) => handleInputChange('longitude', e.target.value)}
              placeholder="e.g. -120.12345"
              disabled={loading}
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="tw-mb-3">
        <Form.Label>Address</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={formData.address}
          onChange={(e) => handleInputChange('address', e.target.value)}
          placeholder="Shown in WordPress map popup"
          disabled={loading}
        />
      </Form.Group>

      <Row className="g-3 tw-mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Phone</Form.Label>
            <Form.Control
              type="text"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="Shown in WordPress map popup"
              disabled={loading}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Fax</Form.Label>
            <Form.Control
              type="text"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="e.g. 250-555-1234 (shown as FAX on public map)"
              disabled={loading}
            />
            <Form.Text className="tw-text-content-muted">
              Shown as Fax on the public WordPress map (stored in the email field for API compatibility).
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="tw-mb-3">
        <Form.Check
          type="checkbox"
          id="foreman-area-omit-wordpress-map"
          label="Omit from public WordPress map and communities list"
          checked={formData.omitFromWordpressMap}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, omitFromWordpressMap: e.target.checked }))
          }
          disabled={loading}
        />
        <Form.Text className="tw-text-content-muted">
          When checked, this foreman area is omitted from the Dawson public site service map (WRAP JSON) and from the communities list.
        </Form.Text>
      </Form.Group>
    </ResponsiveModal>
  );
};

export default ForemanAreaModal;
